import React, { useEffect, useState } from 'react';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { eventAPI } from '../../lib/eventApi';
import MovementBadge from './MovementBadge';

// Read-only history of weeks. Anyone can browse the archive — registered
// or not. When expanded, shows every court for that week so non-registered
// users can also follow what happened. If the calling player WAS on the
// roster that week, their court is highlighted and the row's summary
// shows their personal result.
export default function PlayerWeekHistoryList({ sessionId, currentWeek, myProfileId }) {
  const [weeks, setWeeks] = useState([]);
  const [openWeek, setOpenWeek] = useState(null);
  const [weekDetails, setWeekDetails] = useState({});

  useEffect(() => {
    eventAPI.listWeeks(sessionId)
      .then((r) => setWeeks(r.data?.weeks || []))
      .catch(() => setWeeks([]));
  }, [sessionId]);

  const toggle = async (n) => {
    if (openWeek === n) { setOpenWeek(null); return; }
    setOpenWeek(n);
    if (!weekDetails[n]) {
      try {
        const r = await eventAPI.getWeek(sessionId, n);
        setWeekDetails((prev) => ({ ...prev, [n]: r.data }));
      } catch {/* ignore */}
    }
  };

  if (weeks.length === 0) {
    return (
      <div className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-4 text-center text-sm text-[#6a766e]" data-testid="player-history-empty">
        No weeks yet.
      </div>
    );
  }

  return (
    <ul className="space-y-2" data-testid="player-history-list">
      {weeks.map((w) => {
        const isOpen = openWeek === w.week_number;
        const detail = weekDetails[w.week_number];
        let mySlot = null;
        let myCourt = null;
        if (detail) {
          for (const c of detail.courts || []) {
            for (const s of c.slots) {
              if (s.profile_id === myProfileId) { mySlot = s; myCourt = c; break; }
            }
            if (mySlot) break;
          }
        }
        return (
          <li key={w.id} className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl overflow-hidden" data-testid={`history-row-${w.week_number}`}>
            <button
              type="button"
              onClick={() => toggle(w.week_number)}
              className="w-full px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-[#f0ece3]/40"
              data-testid={`history-row-toggle-${w.week_number}`}
            >
              <div className="flex items-center gap-2">
                {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                <span className="text-sm text-[#013523] font-medium">Week {w.week_number}</span>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  w.status === 'finalized'
                    ? 'bg-[#015e3f]/10 text-[#015e3f]'
                    : 'bg-amber-50 text-amber-700'
                }`}>
                  {w.status.replace(/_/g, ' ')}
                </span>
              </div>
              {mySlot && (
                <div className="flex items-center gap-2 text-[11px] text-[#6a766e]">
                  <span>Court {myCourt.court_number}</span>
                  <span>{mySlot.points != null ? `${mySlot.points} pts` : '—'}</span>
                  {mySlot.movement && <MovementBadge movement={mySlot.movement} label="" />}
                </div>
              )}
            </button>
            {isOpen && (
              <div className="px-3 pb-3 pt-1 text-xs border-t border-[#e3dcd1]/60">
                {!detail && <span className="text-[#6a766e]">Loading…</span>}
                {detail && (detail.courts || []).length === 0 && (
                  <span className="text-[#6a766e]">No courts recorded for this week.</span>
                )}
                {detail && (detail.courts || []).length > 0 && (
                  <div className="space-y-3 mt-2">
                    {(detail.courts || []).map((c) => {
                      const isMyCourt = myProfileId && c.slots.some((s) => s.profile_id === myProfileId);
                      return (
                        <div
                          key={c.id}
                          data-testid={`history-court-${w.week_number}-${c.court_number}`}
                          className={`rounded-lg p-2 ${
                            isMyCourt
                              ? 'bg-[#015e3f]/5 border border-[#015e3f]/30'
                              : 'bg-white border border-[#e3dcd1]'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] font-medium text-[#013523]">
                              Court {c.court_number}
                            </span>
                            {isMyCourt && (
                              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#015e3f] text-white">
                                You
                              </span>
                            )}
                          </div>
                          <ul className="space-y-0.5">
                            {c.slots.map((s) => {
                              const isMe = s.profile_id === myProfileId;
                              return (
                                <li key={s.id} className="flex flex-col gap-0.5">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className={isMe ? 'text-[#015e3f] font-medium' : 'text-[#013523]'}>
                                      {isMe && '→ '}{s.display_name || s.player_name}{isMe && ' (you)'}
                                    </span>
                                    <span className="text-[#6a766e]">{s.points != null ? `${s.points} pts` : '—'}</span>
                                  </div>
                                  {s.is_substitute && s.sub_player_name && (
                                    <span
                                      className="text-[10px] text-[#6a766e] ml-3"
                                      data-testid={`history-sub-note-${s.id}`}
                                    >
                                      Sub: <span className="text-[#013523]">{s.sub_player_name}</span> for {s.player_name}
                                    </span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
